// @ts-nocheck - TODO: Fix type errors and remove cart dependencies in Phase 1
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    MdArrowForward, 
    MdCheckCircle, 
    MdStar, 
    MdSecurity, 
    MdBuild, 
    MdLocalShipping,
    MdSupport,
    MdVerified,
    MdShield,
    MdSpeed,
    MdEco,
    MdPhone,
    MdEmail,
    MdLocationOn,
    MdClose,
    MdCalculate,
    MdShoppingCart,
    MdAdd,
    MdRemove,
    MdPool,
    MdExtension,
    MdViewQuilt
} from 'react-icons/md';
import SeoHead from '../../components/SEO/SeoHead';
import { Organization, WebSite } from '../../components/SEO/StructuredData';
import { SITE_CONFIG } from '../../config/seo';
// Public homepage - no cart or products service needed

// Stub useCart hook for components that reference it (cart functionality removed from public site)
function useCart() {
    return {
        items: [],
        addToCart: () => {
            console.warn('Cart functionality not available on public website');
        }
    };
}

// Stub productsService for components that reference it
const productsService = {
    getProducts: async () => {
        console.warn('Products service not available on public website');
        return { products: [] };
    }
};

// Stub calculatePanelSystemCost function
function calculatePanelSystemCost(pricingData) {
    console.warn('Panel cost calculation not available on public website');
    return {
        summary: {
            totalCost: 0
        }
    };
}

export default function Home() {
    const navigate = useNavigate();
    const [heroIndex, setHeroIndex] = useState(0);
    const [testimonialIndex, setTestimonialIndex] = useState(0);
    const heroSlides = [
        '/home-slideshow/01.jpg',
        '/home-slideshow/02.jpg',
        '/home-slideshow/03.jpg',
        '/home-slideshow/04.jpg',
        '/home-slideshow/05.jpg',
        '/home-slideshow/06.jpg',
        '/home-slideshow/07.jpg',
        '/home-slideshow/08.jpg',
        '/home-slideshow/09.jpg'
    ];

    // Overlay states
    const [showWelcomeOverlay, setShowWelcomeOverlay] = useState(false);

    // Products state (fetched from API)
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [productsError, setProductsError] = useState(null);
    
    // Show welcome overlay on first visit
    useEffect(() => {
        const hasVisited = localStorage.getItem('gramline-visited');
        if (!hasVisited) {
            setShowWelcomeOverlay(true);
            localStorage.setItem('gramline-visited', 'true');
        }
    }, []);

    // Auto-rotate hero slideshow
    useEffect(() => {
        const interval = setInterval(() => {
            setHeroIndex((prev) => (prev + 1) % heroSlides.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [heroSlides.length]);

    // Fetch products from API
    const fetchProducts = useCallback(async () => {
        try {
            setLoadingProducts(true);
            setProductsError(null);

            const params = { pageSize: 25, activeOnly: true, sortBy: 'description', sortOrder: 'asc' };
            const res = await productsService.getProducts(params);

            const list = res && res.data && Array.isArray(res.data.products)
                ? res.data.products
                : Array.isArray(res?.products)
                    ? res.products
                    : Array.isArray(res)
                        ? res
                        : [];

            //

            const mapped = list.map(p => {
                const nameStr = p.name || p.description || p.stockDesc || '';
                let image = '/vite.svg';
                if (/gramline/i.test(nameStr)) image = '/same-colour-ordering/gramline-sheet.jpg';
                else if (/colourline|colorline/i.test(nameStr)) image = '/same-colour-ordering/colourline-sheet.jpg';
                else if (/smart\s*post|smartstile|smart\s*slat/i.test(nameStr)) image = '/same-colour-ordering/extension-smart-post.jpg';
                else if (/channel\s*post/i.test(nameStr)) image = '/same-colour-ordering/extension-channel-post.jpg';
                else if (/rail/i.test(nameStr)) image = '/same-colour-ordering/rail-posts.jpg';

                let badge = null;
                const createdAt = p.createdAt ? new Date(p.createdAt) : null;
                const isRecent = createdAt ? (Date.now() - createdAt.getTime()) <= 1000 * 60 * 60 * 24 * 30 : false;
                if (p.featured) badge = 'Featured';
                else if (isRecent) badge = 'New';

                const colorsArr = typeof p.availableColors === 'string' ? p.availableColors.split(',').map(s => s.trim()) : [];
                const specifications = {};
                if (colorsArr.length) specifications.colors = `${colorsArr.length} Colors Available`;

                return ({
                    id: p.id || p.stockCode || p.sku,
                    name: nameStr,
                    description: p.extendedDescription || p.description || '',
                    category: p.category || '',
                    unitPrice: p.unitPrice ?? p.sellPrice1 ?? 0,
                    image,
                    badge,
                    specifications,
                    availableColors: colorsArr,
                    availableColorHex: typeof p.availableColorHex === 'string'
                        ? p.availableColorHex.split(',').map(s => s.trim())
                        : []
                });
            });

            setProducts(mapped);
        } catch (e) {
            setProductsError(e?.message || 'Failed to load products');
            //
        } finally {
            setLoadingProducts(false);
        }
    }, []);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const formatCurrency = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(n || 0));

    const benefits = [
        {
            icon: <MdShield style={{ fontSize: '48px', color: 'var(--primary-600)' }} />,
            title: 'Australian Made Quality',
            description: 'Proudly manufactured in Australia with premium materials and rigorous quality standards.',
            stat: '30+ Years'
        },
        {
            icon: <MdSpeed style={{ fontSize: '48px', color: 'var(--success-500)' }} />,
            title: 'Fast Installation',
            description: 'Revolutionary SmartPost system reduces installation time significantly.',
            stat: '40% Faster'
        },
        {
            icon: <MdEco style={{ fontSize: '48px', color: 'var(--secondary-600)' }} />,
            title: 'Eco-Friendly',
            description: 'Sustainable manufacturing processes and 100% recyclable materials.',
            stat: '100% Recyclable'
        },
        {
            icon: <MdSupport style={{ fontSize: '48px', color: 'var(--primary-500)' }} />,
            title: 'Expert Support',
            description: 'Professional technical support available when you need it most.',
            stat: '24/7 Support'
        }
    ];

    const testimonials = [
        {
            quote: 'I can not thank Ron the owner enough🙏\n\nOutstanding customer service, top businessman. His product is great\nI’m very happy now as my home looks wonderful with my new fence\nFully recommend 😊',
            name: 'Lucy S',
            location: 'Sydney, NSW',
            rating: 5
        },
        {
            quote: 'Very good customer service service, the staff were very nice and also efficient in supplying my order. 👍👍',
            name: 'Thomas Kuntze',
            location: 'Sydney, NSW',
            rating: 5
        },
        {
            quote: 'Great service today for me. Excellent packaging. Kind staff. Cheap prices. What more do you want? \nThanks guys',
            name: 'Charlotte Lucas',
            location: 'Sydney, NSW',
            rating: 5
        },
        {
            quote: 'Great products, reasonably priced, always in stock. The stand out for them is their customer service, helpful from the counter to the loading dock.',
            name: 'David Turano',
            location: 'Sydney, NSW',
            rating: 5
        }
    ];

    const stats = [
        { number: '30+', label: 'Years Experience' },
        { number: '50,000+', label: 'Projects Completed' },
        { number: '1,000+', label: 'Trade Partners' },
        { number: '24/7', label: 'Support Available' }
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const interval = setInterval(() => {
            setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [testimonials.length]);

    return (
        <>
            <SeoHead
                title="Gramline | No.1 Steel Privacy Fencing Australia"
                description="Australia's leading supplier of steel privacy fencing, security fencing, and pool fencing. Quality products, expert installation, and exceptional service."
                keywords={['steel privacy fencing', 'security fencing', 'pool fencing', 'fencing australia', 'gramline', 'australian made fencing']}
                ogImage="/home-slideshow/01.jpg"
                ogType="website"
                structuredData={
                    <>
                        <Organization
                            name={SITE_CONFIG.name}
                            logo={`${SITE_CONFIG.siteUrl}/light full-logo.png`}
                            url={SITE_CONFIG.siteUrl}
                            sameAs={[]}
                        />
                        <WebSite
                            name={SITE_CONFIG.name}
                            url={SITE_CONFIG.siteUrl}
                        />
                    </>
                }
            />
            <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)' }}>
            {/* Hero Section - Full-bleed slideshow with intro overlay */}
            <section style={{ position: 'relative', height: '80vh', minHeight: '560px', color: 'white', overflow: 'hidden' }}>
                {/* Background slideshow */}
                <div style={{ position: 'absolute', inset: 0 }}>
                    {heroSlides.map((src, index) => (
                        <img
                            key={src}
                            src={src}
                            alt="Hero slideshow"
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                opacity: index === heroIndex ? 1 : 0,
                                transition: 'opacity 1s ease',
                                filter: 'brightness(0.6)'
                            }}
                        />
                    ))}
                </div>

                {/* Intro overlay */}
                <div className="container" style={{ position: 'relative', zIndex: 1, height: '100%' }}>
                    <div style={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        padding: '0 var(--space-5)'
                    }}>
                        <div style={{ maxWidth: '900px' }}>
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                background: 'rgba(255,255,255,0.18)',
                                padding: 'var(--space-2) var(--space-4)',
                                borderRadius: '9999px',
                                marginBottom: 'var(--space-6)',
                                backdropFilter: 'blur(8px)'
                            }}>
                                <MdVerified style={{ fontSize: '18px' }} />
                                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)' }}>
                                    Australian Made
                                </span>
                            </div>

                            <h1 style={{
                                fontSize: 'clamp(2rem, 6vw, 3.5rem)',
                                fontWeight: 'var(--font-weight-bold)',
                                lineHeight: '1.1',
                                margin: '0 0 var(--space-4) 0',
                                textShadow: '0 4px 24px rgba(0,0,0,0.35)'
                            }}>
                                Australia's Premier <br />
                                Steel Privacy Fencing
                            </h1>

                            <p style={{
                                fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                                lineHeight: '1.7',
                                opacity: 0.95,
                                margin: '0 auto var(--space-8) auto',
                                maxWidth: '760px'
                            }}>
                                Transform your property with premium Australian-made steel fencing. Trusted by thousands nationwide for over 30 years.
                            </p>

                            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'center', flexWrap: 'wrap' }}>
                                <Link
                                    to="/products/spf-steel"
                                    className="btn btn-lg"
                                    style={{
                                        background: 'var(--warning-500)',
                                        color: 'var(--text-primary)',
                                        fontWeight: 'var(--font-weight-bold)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        textDecoration: 'none',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)'
                                    }}
                                >
                                    Browse Products
                                    <MdArrowForward />
                                </Link>
                                <Link
                                    to="/purchasing/quote"
                                    className="btn btn-lg btn-outline"
                                    style={{ borderColor: 'rgba(255,255,255,0.7)', color: 'white', textDecoration: 'none' }}
                                >
                                    Get Free Quote
                                    <MdPhone />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Product Navigation Grid - moved above testimonials */}
            <section style={{ padding: 'var(--space-20) 0' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: 'var(--space-12)' }}>
                        <h2 style={{
                            fontSize: 'var(--font-size-4xl)',
                            fontWeight: 'var(--font-weight-bold)',
                            color: 'var(--text-primary)'
                        }}>
                            Explore Products
                        </h2>
                        <p style={{ color: 'var(--text-secondary)', maxWidth: '720px', margin: '0 auto' }}>
                            Jump straight to our most popular product ranges and systems.
                        </p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--space-6)' }}>
                        {[
                            { title: 'SPF® Steel Privacy Fencing', path: '/products/spf-steel', icon: <MdSecurity style={{ fontSize: '36px', color: 'var(--primary-600)' }} /> },
                            { title: 'Steel Plinth™', path: '/products/steel-plinth', icon: <MdBuild style={{ fontSize: '36px', color: 'var(--primary-600)' }} /> },
                            { title: 'GramLat 3D Lattice', path: '/products/gramlat-3d', icon: <MdViewQuilt style={{ fontSize: '36px', color: 'var(--primary-600)' }} /> },
                            { title: 'Gramslat Extension Kit', path: '/customer/accessories', icon: <MdExtension style={{ fontSize: '36px', color: 'var(--primary-600)' }} /> },
                            { title: 'SmartSlat', path: '/customer/special-products/smartslat', icon: <MdBuild style={{ fontSize: '36px', color: 'var(--primary-600)' }} /> },
                            { title: 'Childgard Pool Fencing', path: '/customer/special-products/pool-fencing', icon: <MdPool style={{ fontSize: '36px', color: 'var(--primary-600)' }} /> },
                            { title: 'Childgard Garden Fencing', path: '/products', icon: <MdSecurity style={{ fontSize: '36px', color: 'var(--primary-600)' }} /> },
                            { title: 'Security Fencing & Punched Tubing', path: '/customer/special-products/security-fencing', icon: <MdSecurity style={{ fontSize: '36px', color: 'var(--primary-600)' }} /> },
                            { title: 'Hardware', path: '/customer/hardware-products', icon: <MdBuild style={{ fontSize: '36px', color: 'var(--primary-600)' }} /> },
                            { title: 'Floodway Louvres', path: '/customer/special-products/smartslat', icon: <MdViewQuilt style={{ fontSize: '36px', color: 'var(--primary-600)' }} /> }
                        ].map((item) => (
                            <Link
                                key={item.title}
                                to={item.path}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 'var(--space-3)',
                                    padding: 'var(--space-6)',
                                    background: 'var(--bg-primary)',
                                    border: '1px solid var(--border-light)',
                                    borderRadius: 'var(--radius-xl)',
                                    textDecoration: 'none',
                                    color: 'var(--text-primary)',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                            >
                                {item.icon}
                                <div>
                                    <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)' }}>{item.title}</h3>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--primary-600)', fontSize: 'var(--font-size-sm)' }}>
                                        Explore <MdArrowForward />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features & Benefits Section */}
            <section style={{ padding: 'var(--space-20) 0', background: 'var(--bg-primary)' }}>
                <div className="container">
                    <div style={{
                        background: 'linear-gradient(135deg, var(--primary-50) 0%, var(--bg-primary) 100%)',
                        borderRadius: 'var(--radius-2xl)',
                        padding: 'var(--space-12)',
                        border: '2px solid var(--primary-100)',
                        boxShadow: '0 8px 24px rgba(0, 163, 236, 0.08)',
                        maxWidth: '1000px',
                        margin: '0 auto'
                    }}>
                        <h2 style={{
                            fontSize: 'var(--font-size-3xl)',
                            fontWeight: 'var(--font-weight-bold)',
                            color: 'var(--text-primary)',
                            margin: '0 0 var(--space-8) 0',
                            textAlign: 'center'
                        }}>
                            Features & Benefits
                        </h2>

                        <div style={{
                            marginBottom: 'var(--space-8)',
                            textAlign: 'center'
                        }}>
                            <h3 style={{
                                fontSize: 'var(--font-size-2xl)',
                                fontWeight: 'var(--font-weight-bold)',
                                color: 'var(--primary-700)',
                                margin: '0 0 var(--space-4) 0'
                            }}>
                                Market Leading Innovators
                            </h3>
                            <p style={{
                                fontSize: 'var(--font-size-lg)',
                                lineHeight: '1.7',
                                color: 'var(--text-primary)',
                                marginBottom: 'var(--space-4)'
                            }}>
                                We are the <strong>INVENTOR</strong> not the <strong>COPIER</strong>
                            </p>
                            <p style={{
                                fontSize: 'var(--font-size-base)',
                                lineHeight: '1.7',
                                color: 'var(--text-secondary)'
                            }}>
                                GramLine® is considered to be the No.1 manufacturer and leader in Steel Privacy Fencing in Australia.
                            </p>
                        </div>

                        {/* GramLine was 1st to... */}
                        <div style={{
                            background: 'white',
                            borderRadius: 'var(--radius-xl)',
                            padding: 'var(--space-8)',
                            marginBottom: 'var(--space-8)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{
                                fontSize: 'var(--font-size-xl)',
                                fontWeight: 'var(--font-weight-bold)',
                                color: 'var(--primary-700)',
                                margin: '0 0 var(--space-6) 0',
                                textAlign: 'center',
                                paddingBottom: 'var(--space-4)',
                                borderBottom: '3px solid var(--primary-100)'
                            }}>
                                GramLine® was 1st to
                            </h4>
                            <ul style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: 0,
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                gap: 'var(--space-4)'
                            }}>
                                {[
                                    'Produce the Sawtooth / ZigZag Profile (Aust. Reg. Des. 121344)',
                                    'Put rounded corners on the Channel Posts – for safety',
                                    'Make the inside of the SlimLine™ Channel Posts and Rails the same colour as the exterior',
                                    'Produce an un riveted 3 Dimensional Lattice in Prepainted Hi-Tensile Steel',
                                    'Introduce a Plinth (Australian Patent No. 2004906213) for Steel Privacy Fencing',
                                    'Decide to keep the full Steel Privacy Fencing Colour Range – to assist our customers',
                                    'Introduce the Steel Privacy Fencing colour \'Primrose\' – still the most popular fence colour',
                                    'Introduce the Steel Privacy Fencing colour \'Black\'',
                                    'Emboss our brand onto the product – see our Rails and GramLine® Lattice',
                                    'Produce a stronger, self locating post for easy assembly called the SmartPost®',
                                    'Introduce a new Swinging™ gate kit with no welds'
                                ].map((item, index) => (
                                    <li key={index} style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 'var(--space-3)',
                                        fontSize: 'var(--font-size-sm)',
                                        lineHeight: '1.6',
                                        color: 'var(--text-secondary)',
                                        padding: 'var(--space-3)',
                                        background: 'var(--primary-50)',
                                        borderRadius: 'var(--radius-lg)',
                                        transition: 'all var(--transition-fast)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = 'var(--primary-100)';
                                        e.currentTarget.style.transform = 'translateX(4px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'var(--primary-50)';
                                        e.currentTarget.style.transform = 'translateX(0)';
                                    }}
                                    >
                                        <MdCheckCircle style={{
                                            color: 'var(--primary-600)',
                                            fontSize: '20px',
                                            flexShrink: 0,
                                            marginTop: '2px'
                                        }} />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <p style={{
                            fontSize: 'var(--font-size-base)',
                            lineHeight: '1.8',
                            color: 'var(--text-secondary)',
                            fontStyle: 'italic',
                            textAlign: 'center',
                            maxWidth: '700px',
                            margin: '0 auto'
                        }}>
                            GramLine® is constantly looking at new products, markets and ways to improve the Steel Privacy Fencing Market. One thing you can be assured of is when you choose GramLine®, you choose the No.1.
                        </p>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section style={{ padding: 'var(--space-20) 0', background: 'var(--bg-secondary)' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: 'var(--space-12)' }}>
                        <h2 style={{
                            fontSize: 'var(--font-size-4xl)',
                            fontWeight: 'var(--font-weight-bold)',
                            color: 'var(--text-primary)'
                        }}>
                            What Our Customers Say
                        </h2>
                    </div>

                    <div style={{ maxWidth: '820px', margin: '0 auto', position: 'relative', minHeight: '220px' }}>
                        {testimonials.map((testimonial, index) => (
                            <div
                                key={index}
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    opacity: index === testimonialIndex ? 1 : 0,
                                    transform: index === testimonialIndex ? 'translateY(0)' : 'translateY(16px)',
                                    transition: 'all 0.5s ease',
                                    textAlign: 'center'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-4)' }}>
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <MdStar key={i} style={{ color: 'var(--warning-500)', fontSize: '22px' }} />
                                    ))}
                                </div>
                                <blockquote style={{
                                    fontSize: 'var(--font-size-xl)',
                                    fontStyle: 'italic',
                                    color: 'var(--text-primary)',
                                    marginBottom: 'var(--space-6)',
                                    lineHeight: 'var(--line-height-relaxed)'
                                }}>
                                    "{testimonial.quote}"
                                </blockquote>
                                <div style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--primary-600)' }}>
                                    {testimonial.name}
                                </div>
                                <div style={{ color: 'var(--text-secondary)' }}>
                                    {testimonial.location}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-2)', marginTop: 'var(--space-10)' }}>
                        {testimonials.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setTestimonialIndex(index)}
                                style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    background: index === testimonialIndex ? 'var(--primary-600)' : 'var(--border-medium)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                }}
                            />
                        ))}
                    </div>
                </div>
            </section>
            
        </div>
        </>
    );
}


/**  Product Detail Overlay Component */
function ProductDetailOverlay({ product, onClose }) {
    const { items, addToCart } = useCart();
    const navigate = useNavigate();
    const [config, setConfig] = useState({ quantity: 1 });
    const [openDropdown, setOpenDropdown] = useState(null);

    // Prevent body scroll when overlay is open and handle ESC key
    React.useEffect(() => {
        document.body.style.overflow = 'hidden';
        
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        
        document.addEventListener('keydown', handleEscKey);
        
        return () => {
            document.body.style.overflow = 'unset';
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [onClose]);

    // Generate field configuration based on product type
    const getFieldsForProduct = (product) => {
        const fields = [];

        // Standard size field for most products
        if (product.specifications.sizes && Array.isArray(product.specifications.sizes)) {
            fields.push({
                key: 'size',
                label: 'Size',
                type: 'select',
                options: product.specifications.sizes.map(size => `${size}mm`),
                required: true
            });
        }

        // Types field
        if (product.specifications.types && Array.isArray(product.specifications.types)) {
            fields.push({
                key: 'type',
                label: 'Type',
                type: 'select',
                options: product.specifications.types,
                required: true
            });
        }

        // Color field (required for most products)
        if (product.specifications.colors) {
            fields.push({
                key: 'color',
                label: 'Color',
                type: 'color',
                required: true
            });
        }

        return fields;
    };



    // Custom price calculation
    const calculatePrice = (product, config) => {
        let price = product.basePrice;
        
        // Size-based pricing adjustments
        if (config.size) {
            const sizeValue = parseInt(config.size.replace('mm', ''));
            if (sizeValue >= 2400) price += 15;
            else if (sizeValue >= 2100) price += 10;
            else if (sizeValue >= 1800) price += 5;
        }
        
        // Type-based pricing
        if (config.type === 'Premium') price += 20;
        else if (config.type === 'Heavy Duty') price += 15;
        else if (config.type === 'Professional') price += 25;
        
        return Math.round(price * 100) / 100;
    };



    // Format price function
    const formatPrice = (price) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(price);

    // Generate fields for this product
    const fields = getFieldsForProduct(product);

    // Check if all required specifications (except color) are selected
    const requiredFieldsExceptColor = fields.filter(field => field.required && field.type !== 'color');
    const allRequiredSpecsSelected = requiredFieldsExceptColor.every(field => config[field.key]);
    
    // State for API pricing
    const [apiPrice, setApiPrice] = useState(null);
    const [priceLoading, setPriceLoading] = useState(false);
    
    // Calculate current price - either from API or hide until specs complete
    const basePrice = apiPrice || 0;
    const currentPrice = basePrice * (config.quantity || 1);

    // Generate fields for this product
    // (initialized above to ensure it's available before use)

    // API call for pricing when all required specs (except color) are selected
    React.useEffect(() => {
        if (allRequiredSpecsSelected) {
            setPriceLoading(true);
            
            // Prepare pricing request data
            const pricingData = {
                productId: product.id,
                specifications: {
                    ...config,
                    // Exclude color from pricing calculations
                    color: undefined
                }
            };
            
            // Make API call to get pricing
            fetch('/api/products/calculate-price', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(pricingData)
            })
            .then(response => response.json())
            .then(data => {
                setApiPrice(data.price);
                setPriceLoading(false);
            })
            .catch(error => {
                console.error('Error fetching price:', error);
                // Fallback to base price calculation
                setApiPrice(calculatePrice(product, config));
                setPriceLoading(false);
            });
        } else {
            // Reset price when specs are incomplete
            setApiPrice(null);
            setPriceLoading(false);
        }
    }, [allRequiredSpecsSelected, config, product]);

    // Handle configuration changes
    const handleConfigChange = (field, value) => {
        setConfig(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Close current dropdown after selection
        setOpenDropdown(null);
    };

    // Validation function
    const validateProduct = () => {
        const requiredFields = fields.filter(field => field.required);
        for (const field of requiredFields) {
            if (!config[field.key]) {
                return {
                    isValid: false,
                    message: `Please select ${field.label.toLowerCase()}`
                };
            }
        }
        return { isValid: true };
    };

    // Generate product code
    const generateProductCode = () => {
        let code = product.id.toUpperCase();
        if (config.color) code += '-' + config.color.replace(/\s+/g, '').substring(0, 6);
        if (config.size) code += '-' + config.size;
        return code.substring(0, 20);
    };

    // Add to cart handler
    const handleAddToCart = () => {
        const validation = validateProduct();
        
        if (!validation.isValid) {
            alert(validation.message);
            return;
        }

        const productCode = generateProductCode();
        const price = calculatePrice(product, config);

        const item = {
            id: product.id,
            name: product.name,
            description: product.description,
            image: product.image,
            price: price,
            quantity: config.quantity,
            productCode: productCode,
            config: { ...config }
        };

        addToCart(item, config.quantity);
        setConfig({ quantity: 1 });
    };

    // Direct checkout handler
    const handleDirectCheckout = () => {
        const validation = validateProduct();
        
        if (!validation.isValid) {
            alert(validation.message);
            return;
        }

        const productCode = generateProductCode();
        const price = calculatePrice(product, config);

        const cartItem = {
            id: product.id,
            name: product.name,
            description: product.description,
            image: product.image,
            price: price,
            quantity: config.quantity,
            productCode: productCode,
            subtotal: price * config.quantity,
            source: product.category.toLowerCase().replace(/\s+/g, '-'),
            config: { ...config }
        };

        try {
            localStorage.setItem('directCheckoutItem', JSON.stringify(cartItem));
            navigate('/customer/checkout');
        } catch (error) {
            console.error('Error during direct checkout:', error);
            alert('Failed to proceed to checkout. Please try again.');
        }
    };

    // Quantity change handler
    const handleQuantityChange = (change) => {
        const currentQty = config.quantity || 1;
        const newQty = Math.max(1, currentQty + change);
        handleConfigChange('quantity', newQty);
    };

    // Get validation state
    const validation = validateProduct();

    return (
        <div 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(8px)',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'var(--space-6)'
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            {/* Modern Card Container */}
            <div style={{
                background: 'var(--bg-primary)',
                borderRadius: 'var(--radius-2xl)',
                boxShadow: '0 40px 80px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.08)',
                maxWidth: '1400px',
                width: '100%',
                maxHeight: '85vh',
                overflow: 'hidden',
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: '1.8fr 1fr',
                gap: 0,
                border: '1px solid var(--border-light)'
            }}>
                {/* Close Button */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: 'var(--space-4)',
                        right: 'var(--space-4)',
                        background: 'rgba(0,0,0,0.05)',
                        border: 'none',
                        borderRadius: 'var(--radius-lg)',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        cursor: 'pointer',
                        color: 'var(--text-secondary)',
                        zIndex: 3,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease',
                        backdropFilter: 'blur(8px)'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'var(--danger-500)';
                        e.target.style.color = 'white';
                        e.target.style.transform = 'scale(1.1)';
                        e.target.style.boxShadow = '0 4px 16px rgba(220, 38, 38, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(0,0,0,0.05)';
                        e.target.style.color = 'var(--text-secondary)';
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                    title="Close (Esc)"
                >
                    <MdClose />
                </button>

                {/* Left Panel - Product Details */}
                <div style={{
                    padding: 'var(--space-8)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    background: 'var(--bg-primary)',
                    maxHeight: '85vh',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'var(--border-medium) transparent',
                    borderRight: '1px solid var(--border-light)'
                }}>
                    {/* Product Header */}
                    <div style={{
                        marginBottom: 'var(--space-6)'
                    }}>
                        {/* Product Image */}
                        <div style={{
                            width: '100%',
                            height: '200px',
                            background: `linear-gradient(135deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 100%), url(${product.image})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            borderRadius: 'var(--radius-lg)',
                            marginBottom: 'var(--space-4)',
                            position: 'relative',
                            border: '1px solid var(--border-light)',
                            overflow: 'hidden',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                        }}>
                            {/* Badges */}
                            <div style={{
                                position: 'absolute',
                                top: 'var(--space-3)',
                                left: 'var(--space-3)',
                                display: 'flex',
                                gap: 'var(--space-2)'
                            }}>
                                <span style={{
                                    background: 'rgba(0,0,0,0.8)',
                                    color: 'white',
                                    padding: 'var(--space-1) var(--space-3)',
                                    borderRadius: 'var(--radius-full)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    backdropFilter: 'blur(12px)',
                                    border: '1px solid rgba(255,255,255,0.1)'
                                }}>
                                    {product.category}
                                </span>
                                {product.badge && (
                                    <span style={{
                                        background: 'var(--warning-400)',
                                        color: 'var(--text-primary)',
                                        padding: 'var(--space-1) var(--space-3)',
                                        borderRadius: 'var(--radius-full)',
                                        fontSize: 'var(--font-size-xs)',
                                        fontWeight: 'var(--font-weight-bold)',
                                        backdropFilter: 'blur(12px)'
                                    }}>
                                        {product.badge}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Product Info */}
                        <div>
                            <h2 style={{
                                fontSize: 'var(--font-size-xl)',
                                fontWeight: 'var(--font-weight-bold)',
                                color: 'var(--text-primary)',
                                margin: '0 0 var(--space-3) 0',
                                lineHeight: '1.3'
                            }}>
                                {product.name}
                    </h2>
                    
                        </div>
                    </div>



                    {/* Configuration Section */}
                                         
                     <div style={{
                         display: 'grid',
                         gap: 'var(--space-2)',
                         marginTop: 'var(--space-3)'
                     }}>
                         {fields.map(field => (
                             <div key={field.key} style={{
                                 position: 'relative'
                             }}>
                                 <label style={{
                                     display: 'block',
                                     fontSize: 'var(--font-size-sm)',
                                     fontWeight: 'var(--font-weight-semibold)',
                                     color: 'var(--text-primary)',
                                     marginBottom: 'var(--space-1)'
                                 }}>
                                     {field.label} {field.required && <span style={{ color: 'var(--danger-500)' }}>*</span>}
                                 </label>
                                 
                                 {field.type === 'select' ? (
                                     <select
                                         value={config[field.key] || ''}
                                         onChange={(e) => handleConfigChange(field.key, e.target.value)}
                                         onFocus={() => setOpenDropdown(field.key)}
                                         onBlur={() => setOpenDropdown(null)}
                                         style={{
                                             width: '100%',
                                             padding: 'var(--space-3)',
                                             border: '1px solid var(--border-medium)',
                                             borderRadius: 'var(--radius-lg)',
                                             fontSize: 'var(--font-size-sm)',
                                             background: 'var(--bg-primary)',
                                             color: 'var(--text-primary)',
                                             transition: 'all 0.2s ease'
                                         }}
                                         onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-400)'}
                                         onMouseLeave={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                                     >
                                         <option value="">{field.placeholder || `Select ${field.label}`}</option>
                                         {field.options.map(option => (
                                             <option key={option} value={option}>{option}</option>
                                         ))}
                                     </select>
                                 ) : field.type === 'color' ? (
                                     <ColorSelectField
                                         value={config[field.key] || ''}
                                         onChange={(value) => handleConfigChange(field.key, value)}
                                         isOpen={openDropdown === field.key}
                                         onFocus={() => setOpenDropdown(field.key)}
                                         onClose={() => setOpenDropdown(null)}
                                     />
                                 ) : null}
                             </div>
                         ))}
                     </div>
                    
                    {/* Pricing Summary */}
                    {allRequiredSpecsSelected ? (
                        <div style={{
                        background: 'linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%)',
                        padding: 'var(--space-6)',
                        borderRadius: 'var(--radius-xl)',
                        border: '2px solid var(--primary-200)',
                        marginTop: 'var(--space-4)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-4)'
                            }}>
                                <span style={{
                                    fontSize: 'var(--font-size-lg)',
                                    color: 'var(--text-primary)',
                                    fontWeight: 'var(--font-weight-bold)'
                                }}>
                                    Total:
                                </span>
                                
                                {/* Quantity Controls */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    background: 'var(--bg-primary)',
                                    padding: 'var(--space-2)',
                                    borderRadius: 'var(--radius-lg)',
                                    border: '1px solid var(--primary-200)'
                                }}>
                                    <button
                                        onClick={() => handleQuantityChange(-1)}
                                        style={{
                                            padding: 'var(--space-2)',
                                            borderRadius: 'var(--radius-md)',
                                            border: 'none',
                                            background: 'var(--bg-secondary)',
                                            color: 'var(--text-primary)',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 'var(--font-size-sm)',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => e.target.style.background = 'var(--border-medium)'}
                                        onMouseLeave={(e) => e.target.style.background = 'var(--bg-secondary)'}
                                    >
                                        <MdRemove size={16} />
                                    </button>
                                    <span style={{
                                        fontSize: 'var(--font-size-base)',
                                        fontWeight: 'var(--font-weight-semibold)',
                                        color: 'var(--text-primary)',
                                        minWidth: '32px',
                                        textAlign: 'center'
                                    }}>
                                        {config.quantity}
                                    </span>
                                    <button
                                        onClick={() => handleQuantityChange(1)}
                                        style={{
                                            padding: 'var(--space-2)',
                                            borderRadius: 'var(--radius-md)',
                                            border: 'none',
                                            background: 'var(--bg-secondary)',
                                            color: 'var(--text-primary)',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 'var(--font-size-sm)',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => e.target.style.background = 'var(--border-medium)'}
                                        onMouseLeave={(e) => e.target.style.background = 'var(--bg-secondary)'}
                                    >
                                        <MdAdd size={16} />
                                    </button>
                                </div>
                            </div>
                            
                            <span style={{
                                fontSize: 'var(--font-size-xl)',
                                color: 'var(--primary-600)',
                                fontWeight: 'var(--font-weight-bold)'
                            }}>
                                {priceLoading ? 'Loading...' : formatPrice(currentPrice)}
                            </span>
                        </div>
                    </div>
                    ) : (
                        <div style={{
        background: 'var(--bg-secondary)',
        padding: 'var(--space-6)',
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--border-light)',
        marginTop: 'var(--space-4)',
        textAlign: 'center'
    }}>
        <span style={{
            fontSize: 'var(--font-size-base)',
            color: 'var(--text-secondary)',
            fontStyle: 'italic'
        }}>
            Complete all specifications to see pricing
        </span>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 'var(--space-4)',
                        marginTop: 'var(--space-6)'
                    }}>
                        <button
                            onClick={handleAddToCart}
                            disabled={!validation.isValid}
                            style={{
                                padding: 'var(--space-4)',
                                borderRadius: 'var(--radius-xl)',
                                border: 'none',
                                background: validation.isValid ? 'var(--primary-600)' : 'var(--bg-tertiary)',
                                color: validation.isValid ? 'white' : 'var(--text-tertiary)',
                                fontSize: 'var(--font-size-base)',
                                fontWeight: 'var(--font-weight-semibold)',
                                cursor: validation.isValid ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                        justifyContent: 'center',
                                gap: 'var(--space-2)',
                                transition: 'all 0.2s ease',
                                boxShadow: validation.isValid ? '0 4px 12px rgba(0,0,0,0.1)' : 'none'
                            }}
                            onMouseEnter={(e) => {
                                if (validation.isValid) {
                                    e.target.style.background = 'var(--primary-700)';
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 6px 16px rgba(0,0,0,0.15)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (validation.isValid) {
                                    e.target.style.background = 'var(--primary-600)';
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                }
                            }}
                        >
                            <MdShoppingCart size={18} />
                            Add to Cart
                        </button>
                        <button
                            onClick={handleDirectCheckout}
                            disabled={!validation.isValid}
                            style={{
                                padding: 'var(--space-4)',
                                borderRadius: 'var(--radius-xl)',
                                border: validation.isValid ? '2px solid var(--success-700)' : '2px solid var(--border-light)',
                                background: 'transparent',
                                color: validation.isValid ? 'var(--success-500)' : 'var(--text-tertiary)',
                                fontSize: 'var(--font-size-base)',
                                fontWeight: 'var(--font-weight-semibold)',
                                cursor: validation.isValid ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 'var(--space-2)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                if (validation.isValid) {
                                    e.target.style.background = 'var(--success-700)';
                                    e.target.style.color = 'white';
                                    e.target.style.transform = 'translateY(-1px)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (validation.isValid) {
                                    e.target.style.background = 'transparent';
                                    e.target.style.color = 'var(--success-700)';
                                    e.target.style.transform = 'translateY(0)';
                                }
                            }}
                        >
                            <MdArrowForward size={18} />
                            Checkout Now
                        </button>
                    </div>

                    
                </div>

                {/* Right Panel - Shopping Cart */}
                <div style={{
                    background: 'linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%)',
                    borderLeft: '1px solid var(--border-light)',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Cart Header */}
                    <div style={{
                        padding: 'var(--space-6)',
                        borderBottom: '1px solid var(--border-light)',
                        background: 'linear-gradient(135deg, var(--primary-600) 0%, var(--primary-700) 100%)',
                        color: 'white'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-3)'
                            }}>
                                <div style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    borderRadius: 'var(--radius-lg)',
                                    padding: 'var(--space-2)'
                                }}>
                                    <MdShoppingCart style={{ fontSize: '20px' }} />
                                </div>
                                <div>
                                    <h3 style={{
                                        fontSize: 'var(--font-size-lg)',
                                        fontWeight: 'var(--font-weight-bold)',
                                        margin: 0
                                    }}>
                                        Shopping Cart
                                    </h3>
                                    <p style={{
                                        fontSize: 'var(--font-size-sm)',
                                        margin: 0,
                                        opacity: 0.9
                                    }}>
                                        {items.length} {items.length === 1 ? 'item' : 'items'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Cart Content */}
                    <div style={{ 
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {items.length > 0 ? (
                            <div style={{ padding: 'var(--space-4)' }}>
                                {/* Cart items would be displayed here */}
                                <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>
                                    {items.length} item(s) in cart
                                </p>
                            </div>
                        ) : (
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 'var(--space-8)',
                                textAlign: 'center'
                            }}>
                                <div style={{
                                    width: '80px',
                                    height: '80px',
                                    background: 'linear-gradient(135deg, var(--primary-100) 0%, var(--primary-200) 100%)',
                                    borderRadius: 'var(--radius-2xl)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: 'var(--space-6)',
                                    border: '1px solid var(--primary-200)'
                                }}>
                                    <MdShoppingCart style={{
                                        fontSize: '32px',
                                        color: 'var(--primary-600)'
                                    }} />
                                </div>
                                <h4 style={{
                                    fontSize: 'var(--font-size-lg)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: 'var(--text-primary)',
                                    marginBottom: 'var(--space-3)'
                                }}>
                                    Your cart is empty
                                </h4>
                                <p style={{
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--text-secondary)',
                                    margin: 0,
                                    lineHeight: '1.5',
                                    maxWidth: '200px'
                                }}>
                                    Configure the product options and add items to your cart
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// Product Configuration Section Component (extracted from ProductCard)
function ProductConfigurationSection({ product, fields, onValidate, onGenerateProductCode, onPriceCalculate, source }) {
    const { addToCart } = useCart();
    const navigate = useNavigate();
    const [config, setConfig] = useState({ quantity: 1 });
    const buttonRef = useRef(null);

    // Available colors from the system
    const colors = [
        'Caulfield Green', 'Monument', 'Woodland Grey', 'Surfmist', 'Basalt', 'Jasper', 
        'Pale Eucalypt', 'Classic Cream', 'Terrain', 'Night Sky', 'Ironstone', 'Shale Grey', 
        'Dune', 'Evening Haze', 'Cove', 'Gully', 'Mangrove', 'Windspray', 'Wallaby', 
        'Deep Ocean', 'Cottage Green', 'Paperbark', 'Manor Red', 'Loft', 'Domain', 
        'Platinum', 'Galvanised'
    ];

    // Color hex values for visual preview
    const colorHex = {
        'Caulfield Green': '#2B5B31',
        'Monument': '#4A4A4A',
        'Woodland Grey': '#6B7B6B',
        'Surfmist': '#E8E8E8',
        'Basalt': '#2C2C2C',
        'Jasper': '#8B4513',
        'Pale Eucalypt': '#A8C090',
        'Classic Cream': '#F5F5DC',
        'Terrain': '#8B7355',
        'Night Sky': '#1C1C2E',
        'Ironstone': '#5C5C5C',
        'Shale Grey': '#8A8A8A',
        'Dune': '#C4A373',
        'Evening Haze': '#B6B8C4',
        'Cove': '#4A6B7C',
        'Gully': '#7A8471',
        'Mangrove': '#5B7B4F',
        'Windspray': '#C5D3D8',
        'Wallaby': '#8B7B5C',
        'Deep Ocean': '#2E4057',
        'Cottage Green': '#3B5B3F',
        'Paperbark': '#A0956B',
        'Manor Red': '#8B2635',
        'Loft': '#707070',
        'Domain': '#2F2F2F',
        'Platinum': '#D3D3D3',
        'Galvanised': '#B8B8B8'
    };

    const handleConfigChange = (field, value) => {
        setConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleQuantityChange = (change) => {
        const currentQty = config.quantity || 1;
        const newQty = Math.max(1, currentQty + change);
        handleConfigChange('quantity', newQty);
    };

    const validateProduct = () => {
        if (onValidate) {
            return onValidate(config);
        }
        
        const requiredFields = fields.filter(field => field.required);
        for (const field of requiredFields) {
            if (!config[field.key]) {
                return {
                    isValid: false,
                    message: `Please select ${field.label.toLowerCase()}`
                };
            }
        }
        return { isValid: true };
    };

    const generateProductCode = () => {
        if (onGenerateProductCode) {
            return onGenerateProductCode(product, config);
        }
        
        let code = product.id.toUpperCase();
        if (config.color) code += '-' + config.color.replace(/\s+/g, '').substring(0, 6);
        if (config.size) code += '-' + config.size;
        return code.substring(0, 20);
    };

    const calculatePrice = () => {
        if (onPriceCalculate) {
            return onPriceCalculate(product, config);
        }
        return product.basePrice || 0;
    };

    const formatPrice = (price) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(price);

    const handleAddToCart = () => {
        const validation = validateProduct();
        
        if (!validation.isValid) {
            alert(validation.message);
            return;
        }

        const productCode = generateProductCode();
        const price = calculatePrice();

        const item = {
            id: product.id,
            name: product.name,
            description: product.description,
            image: product.image,
            price: price,
            quantity: config.quantity,
            productCode: productCode,
            config: { ...config }
        };

        addToCart(item, config.quantity);
        setConfig({ quantity: 1 });
    };

    const handleDirectCheckout = () => {
        const validation = validateProduct();
        
        if (!validation.isValid) {
            alert(validation.message);
            return;
        }

        const productCode = generateProductCode();
        const price = calculatePrice();

        const cartItem = {
            id: product.id,
            name: product.name,
            description: product.description,
            image: product.image,
            price: price,
            quantity: config.quantity,
            productCode: productCode,
            subtotal: price * config.quantity,
            source: source,
            config: { ...config }
        };

        try {
            localStorage.setItem('directCheckoutItem', JSON.stringify(cartItem));
            navigate('/customer/checkout');
        } catch (error) {
            console.error('Error during direct checkout:', error);
            alert('Failed to proceed to checkout. Please try again.');
        }
    };

    const validation = validateProduct();
    const currentPrice = calculatePrice();

    return (
        <div>
            {/* Product Image */}
            <div style={{
                width: '100%',
                height: '200px',
                background: product.image ? `url(${product.image})` : 'linear-gradient(135deg, var(--primary-100) 0%, var(--primary-200) 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: 'var(--radius-lg)',
                marginBottom: 'var(--space-4)',
                position: 'relative',
                border: '1px solid var(--border-light)'
            }}>
                {/* Category and Badge Overlay */}
                <div style={{
                    position: 'absolute',
                    top: 'var(--space-3)',
                    left: 'var(--space-3)',
                    display: 'flex',
                    gap: 'var(--space-2)'
                }}>
                    <span style={{
                        background: 'rgba(0,0,0,0.7)',
                            color: 'white',
                        padding: 'var(--space-1) var(--space-2)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-medium)'
                    }}>
                        {product.category}
                    </span>
                    {product.badge && (
                        <span style={{
                            background: 'var(--warning-500)',
                            color: 'var(--text-primary)',
                            padding: 'var(--space-1) var(--space-2)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: 'var(--font-weight-bold)'
                        }}>
                            {product.badge}
                        </span>
                    )}
                </div>
                    </div>

            {/* Product Info */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
                <h3 style={{
                    fontSize: 'var(--font-size-xl)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--text-primary)',
                    marginBottom: 'var(--space-2)',
                    lineHeight: '1.3'
                }}>
                    {product.name}
                </h3>
                
                <p style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-secondary)',
                    margin: '0 0 var(--space-3) 0',
                    lineHeight: '1.4'
                }}>
                    {product.description}
                </p>

                <div style={{
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--primary-600)'
                }}>
                    {formatPrice(currentPrice)}
                </div>
            </div>

            {/* Configuration Fields */}
                    <div style={{
                        display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)',
                marginBottom: 'var(--space-4)'
            }}>
                {fields.map(field => (
                    <div key={field.key}>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 'var(--font-weight-medium)',
                            color: 'var(--text-primary)',
                            marginBottom: 'var(--space-2)'
                        }}>
                            {field.label} {field.required && '*'}
                        </label>
                        
                        {field.type === 'select' ? (
                            <select
                                value={config[field.key] || ''}
                                onChange={(e) => handleConfigChange(field.key, e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: 'var(--space-3)',
                                    border: '1px solid var(--border-medium)',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: 'var(--font-size-sm)',
                                    background: 'var(--bg-primary)',
                                    color: 'var(--text-primary)'
                                }}
                            >
                                <option value="">{field.placeholder || `Select ${field.label}`}</option>
                                {field.options.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        ) : field.type === 'color' ? (
                            <ColorSelectField
                                value={config[field.key] || ''}
                                onChange={(value) => handleConfigChange(field.key, value)}
                            />
                        ) : null}
                    </div>
                ))}

                {/* Quantity Controls */}
                <div>
                    <label style={{
                        display: 'block',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--text-primary)',
                        marginBottom: 'var(--space-2)'
                    }}>
                        Quantity
                    </label>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3)'
                    }}>
                        <button
                            onClick={() => handleQuantityChange(-1)}
                            style={{
                                padding: 'var(--space-2)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--border-medium)',
                                background: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                        justifyContent: 'center',
                                fontSize: 'var(--font-size-sm)'
                            }}
                        >
                            <MdRemove size={16} />
                        </button>
                        <span style={{
                            fontSize: 'var(--font-size-base)',
                            fontWeight: 'var(--font-weight-medium)',
                            color: 'var(--text-primary)',
                            minWidth: '30px',
                            textAlign: 'center'
                        }}>
                            {config.quantity}
                        </span>
                        <button
                            onClick={() => handleQuantityChange(1)}
                            style={{
                                padding: 'var(--space-2)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--border-medium)',
                                background: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                cursor: 'pointer',
                                display: 'flex',
                        alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 'var(--font-size-sm)'
                            }}
                        >
                            <MdAdd size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-3)'
            }}>
                <button
                    onClick={handleAddToCart}
                    disabled={!validation.isValid}
                    style={{
                        flex: 1,
                        padding: 'var(--space-3)',
                        borderRadius: 'var(--radius-md)',
                        border: 'none',
                        background: validation.isValid ? 'var(--primary-600)' : 'var(--bg-secondary)',
                        color: validation.isValid ? 'white' : 'var(--text-tertiary)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        cursor: validation.isValid ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--space-2)',
                        transition: 'all 0.2s ease'
                    }}
                >
                    <MdShoppingCart size={16} />
                    Add to Cart
                </button>
                <button
                    onClick={handleDirectCheckout}
                    disabled={!validation.isValid}
                    style={{
                        flex: 1,
                        padding: 'var(--space-3)',
                        borderRadius: 'var(--radius-md)',
                        border: 'none',
                        background: validation.isValid ? 'var(--success-600)' : 'var(--bg-secondary)',
                        color: validation.isValid ? 'white' : 'var(--text-tertiary)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        cursor: validation.isValid ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--space-2)',
                        transition: 'all 0.2s ease'
                    }}
                >
                    Checkout
                </button>
                        </div>
                        </div>
    );
}

// Custom Color Select Field Component
function ColorSelectField({ value, onChange, isOpen: externalIsOpen, onFocus, onClose }) {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    
    // Use external state if provided, otherwise use internal state
    const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
    const setIsOpen = externalIsOpen !== undefined ? (open) => {
        if (open && onFocus) onFocus();
        if (!open && onClose) onClose();
    } : setInternalIsOpen;
    
    // Available colors from the system
    const colors = [
        'Caulfield Green', 'Monument', 'Woodland Grey', 'Surfmist', 'Basalt', 'Jasper', 
        'Pale Eucalypt', 'Classic Cream', 'Terrain', 'Night Sky', 'Ironstone', 'Shale Grey', 
        'Dune', 'Evening Haze', 'Cove', 'Gully', 'Mangrove', 'Windspray', 'Wallaby', 
        'Deep Ocean', 'Cottage Green', 'Paperbark', 'Manor Red', 'Loft', 'Domain', 
        'Platinum', 'Galvanised'
    ];

    // Color hex values for visual preview
    const colorHex = {
        'Caulfield Green': '#2B5B31',
        'Monument': '#4A4A4A',
        'Woodland Grey': '#6B7B6B',
        'Surfmist': '#E8E8E8',
        'Basalt': '#2C2C2C',
        'Jasper': '#8B4513',
        'Pale Eucalypt': '#A8C090',
        'Classic Cream': '#F5F5DC',
        'Terrain': '#8B7355',
        'Night Sky': '#1C1C2E',
        'Ironstone': '#5C5C5C',
        'Shale Grey': '#8A8A8A',
        'Dune': '#C4A373',
        'Evening Haze': '#B6B8C4',
        'Cove': '#4A6B7C',
        'Gully': '#7A8471',
        'Mangrove': '#5B7B4F',
        'Windspray': '#C5D3D8',
        'Wallaby': '#8B7B5C',
        'Deep Ocean': '#2E4057',
        'Cottage Green': '#3B5B3F',
        'Paperbark': '#A0956B',
        'Manor Red': '#8B2635',
        'Loft': '#707070',
        'Domain': '#2F2F2F',
        'Platinum': '#D3D3D3',
        'Galvanised': '#B8B8B8'
    };
    
    const [filteredOptions, setFilteredOptions] = useState(colors);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const buttonRef = useRef(null);
    
    // Filter colors based on search input
    useEffect(() => {
        if (searchFilter) {
            const filtered = colors.filter(color => 
                color.toLowerCase().includes(searchFilter.toLowerCase())
            );
            setFilteredOptions(filtered);
        } else {
            setFilteredOptions(colors);
        }
    }, [searchFilter, colors]);

    // Reset filter when dropdown closes
    useEffect(() => {
        if (!isOpen) {
            setSearchFilter('');
        }
    }, [isOpen]);

    // Calculate dropdown position when opening
    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const updatePosition = () => {
                const rect = buttonRef.current.getBoundingClientRect();
                setDropdownPosition({
                    top: rect.bottom + window.scrollY + 2,
                    left: rect.left + window.scrollX,
                    width: rect.width
                });
            };

            updatePosition();
            window.addEventListener('scroll', updatePosition);
            window.addEventListener('resize', updatePosition);

            return () => {
                window.removeEventListener('scroll', updatePosition);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [isOpen]);

    // Handle keyboard input when dropdown is open
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!isOpen) return;
            
            if (event.key.length === 1 && /[a-zA-Z]/.test(event.key)) {
                event.preventDefault();
                setSearchFilter(prev => prev + event.key.toLowerCase());
            } else if (event.key === 'Backspace') {
                event.preventDefault();
                setSearchFilter(prev => prev.slice(0, -1));
            } else if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, searchFilter]);
    
    const handleColorSelect = (color) => {
        onChange(color);
        setIsOpen(false);
    };

    return (
        <div style={{ position: 'relative' }}>
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    border: '1px solid var(--border-medium)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    background: 'var(--bg-primary)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-400)'}
                onMouseLeave={(e) => e.target.style.borderColor = 'var(--border-medium)'}
            >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: 'var(--radius-sm)',
                        background: value ? colorHex[value] || '#ccc' : '#ccc',
                        border: '1px solid var(--border-light)',
                        flexShrink: 0
                    }} />
                    <span style={{ color: value ? 'var(--text-primary)' : 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                        {value || 'Select Color'}
                    </span>
                        </div>
                <svg 
                    style={{ 
                        width: '16px', 
                        height: '16px',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                >
                    <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
            </button>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-light)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                    zIndex: 1000,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    marginTop: '4px'
                }}>
                    {searchFilter && (
                        <div style={{
                            padding: 'var(--space-2)',
                            background: 'var(--primary-50)',
                            borderBottom: '1px solid var(--border-light)',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--primary-700)',
                            fontStyle: 'italic'
                        }}>
                            Filtering: "{searchFilter}" ({filteredOptions.length} colors)
                    </div>
                    )}

                    <div
                        onClick={() => handleColorSelect('')}
                        style={{
                            padding: 'var(--space-2)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            borderBottom: '1px solid var(--border-light)',
                            color: 'var(--text-secondary)',
                            fontStyle: 'italic',
                            fontSize: 'var(--font-size-sm)'
                        }}
                        onMouseEnter={(e) => e.target.style.background = 'var(--bg-secondary)'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                        <div style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: 'var(--radius-sm)',
                            background: 'transparent',
                            border: '1px dashed var(--border-medium)',
                            flexShrink: 0
                        }} />
                        <span>Select a color</span>
                </div>

                    {filteredOptions.length === 0 && searchFilter && (
                        <div style={{
                            padding: 'var(--space-3)',
                            textAlign: 'center',
                            color: 'var(--text-secondary)',
                            fontStyle: 'italic',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            No colors found matching "{searchFilter}"
                        </div>
                    )}

                    {filteredOptions.map(color => (
                        <div
                            key={color}
                            onClick={() => handleColorSelect(color)}
                            style={{
                                padding: 'var(--space-2)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                borderBottom: '1px solid var(--border-light)',
                                background: value === color ? 'var(--primary-50)' : 'transparent',
                                fontSize: 'var(--font-size-sm)'
                            }}
                            onMouseEnter={(e) => {
                                if (value !== color) {
                                    e.target.style.background = 'var(--bg-secondary)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (value !== color) {
                                    e.target.style.background = 'transparent';
                                } else {
                                    e.target.style.background = 'var(--primary-50)';
                                }
                            }}
                        >
                            <div style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: 'var(--radius-sm)',
                                background: colorHex[color] || '#ccc',
                                border: '1px solid var(--border-light)',
                                flexShrink: 0
                            }} />
                            <span style={{
                                color: 'var(--text-primary)',
                                fontWeight: value === color ? 'var(--font-weight-medium)' : 'var(--font-weight-normal)'
                            }}>
                                {color}
                            </span>
                            {value === color && (
                                <svg 
                                    style={{ 
                                        width: '16px', 
                                        height: '16px',
                                        color: 'var(--primary-600)',
                                        marginLeft: 'auto'
                                    }}
                                    fill="currentColor" 
                                    viewBox="0 0 20 20"
                                >
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// Embedded Panel Calculator Component
function PanelCalculatorEmbed({ onClose }) {
    const [calculationMethod, setCalculationMethod] = useState('meterage');
    const [formData, setFormData] = useState({
        totalMeterage: '',
        numberOfPanels: '',
        panelHeight: 1790,
        sheetType: 'Gramline (ZIG ZAG)',
        sheetColour: 'Caulfield Green',
        frameColour: 'Caulfield Green',
        postType: 'Channel Post',
        postHeight: 1800
    });
    const [results, setResults] = useState({
        numberOfPanels: 0,
        totalMeterage: 0,
        numberOfPosts: 0,
        totalCost: 0,
        panels2370: 0,
        panels1560: 0,
        maxCover: 0,
        sheets: 0,
        posts: 0,
        rail2370: 0,
        rail3100: 0,
        screws: 0
    });

    const heightOptions = [890, 1190, 1340, 1490, 1640, 1790, 2090, 2390];
    const sheetTypes = ['Gramline (ZIG ZAG)', 'Colourline (5 RIB)'];
    const postTypes = ['Channel Post', 'Smart Post'];
    const postHeightOptions = [1800, 2100, 2400, 2700, 3000];
    const colorOptions = ['Caulfield Green', 'Monument', 'Woodland Grey', 'Surfmist', 'Basalt'];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const calculateResults = useCallback(() => {
        if (calculationMethod === 'meterage') {
            const totalMeterage = parseFloat(formData.totalMeterage) || 0;
            if (totalMeterage <= 0) return;

            const panels2370 = Math.ceil(totalMeterage / 2.37);
            const totalSheets = panels2370 * 3;
            const totalPosts = panels2370 + 1;
            const rails2370 = panels2370 * 2;

            const pricingData = {
                panelHeight: parseInt(formData.panelHeight),
                sheetType: formData.sheetType,
                sheetColour: formData.sheetColour,
                frameColour: formData.frameColour,
                postType: formData.postType,
                postHeight: parseInt(formData.postHeight),
                sheets: totalSheets,
                posts: totalPosts,
                rail2370: rails2370,
                rail3100: 0,
                screws: panels2370 * 18,
                panels2370: panels2370,
                panels1560: 0,
                totalMeterage: totalMeterage
            };

            const costBreakdown = calculatePanelSystemCost(pricingData);

            setResults({
                numberOfPanels: panels2370,
                totalMeterage: totalMeterage,
                numberOfPosts: totalPosts,
                totalCost: costBreakdown.summary.totalCost,
                panels2370: panels2370,
                panels1560: 0,
                maxCover: panels2370 * 2.37,
                sheets: totalSheets,
                posts: totalPosts,
                rail2370: rails2370,
                rail3100: 0,
                screws: panels2370 * 18
            });
        }
    }, [formData, calculationMethod]);

    useEffect(() => {
        calculateResults();
    }, [calculateResults]);

    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--space-6)'
        }}>
            {/* Left Column - Form */}
            <div>
                <div style={{
                    display: 'flex',
                    gap: 'var(--space-2)',
                    marginBottom: 'var(--space-6)'
                }}>
                    <button
                        onClick={() => setCalculationMethod('meterage')}
                        style={{
                            padding: 'var(--space-3) var(--space-4)',
                            border: calculationMethod === 'meterage' ? '2px solid var(--primary-600)' : '1px solid var(--border-medium)',
                            background: calculationMethod === 'meterage' ? 'var(--primary-50)' : 'var(--bg-primary)',
                            color: calculationMethod === 'meterage' ? 'var(--primary-600)' : 'var(--text-secondary)',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 'var(--font-weight-medium)'
                        }}
                    >
                        By Meterage
                    </button>
                    <button
                        onClick={() => setCalculationMethod('panels')}
                        style={{
                            padding: 'var(--space-3) var(--space-4)',
                            border: calculationMethod === 'panels' ? '2px solid var(--primary-600)' : '1px solid var(--border-medium)',
                            background: calculationMethod === 'panels' ? 'var(--primary-50)' : 'var(--bg-primary)',
                            color: calculationMethod === 'panels' ? 'var(--primary-600)' : 'var(--text-secondary)',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 'var(--font-weight-medium)'
                        }}
                    >
                        By Panels
                    </button>
                </div>

                <div style={{
                    display: 'grid',
                    gap: 'var(--space-4)'
                }}>
                    {calculationMethod === 'meterage' ? (
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 'var(--font-weight-medium)',
                                marginBottom: 'var(--space-2)'
                            }}>
                                Total Meterage (m)
                            </label>
                            <input
                                type="number"
                                name="totalMeterage"
                                value={formData.totalMeterage}
                                onChange={handleInputChange}
                                placeholder="Enter total meterage"
                                style={{
                                    width: '100%',
                                    padding: 'var(--space-3)',
                                    border: '1px solid var(--border-medium)',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: 'var(--font-size-base)'
                                }}
                            />
                        </div>
                    ) : (
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 'var(--font-weight-medium)',
                                marginBottom: 'var(--space-2)'
                            }}>
                                Number of Panels
                            </label>
                            <input
                                type="number"
                                name="numberOfPanels"
                                value={formData.numberOfPanels}
                                onChange={handleInputChange}
                                placeholder="Enter number of panels"
                                style={{
                                    width: '100%',
                                    padding: 'var(--space-3)',
                                    border: '1px solid var(--border-medium)',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: 'var(--font-size-base)'
                                }}
                            />
                        </div>
                    )}

                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 'var(--font-weight-medium)',
                            marginBottom: 'var(--space-2)'
                        }}>
                            Panel Height
                        </label>
                        <select
                            name="panelHeight"
                            value={formData.panelHeight}
                            onChange={handleInputChange}
                            style={{
                                width: '100%',
                                padding: 'var(--space-3)',
                                border: '1px solid var(--border-medium)',
                                borderRadius: 'var(--radius-md)',
                                fontSize: 'var(--font-size-base)'
                            }}
                        >
                            {heightOptions.map(height => (
                                <option key={height} value={height}>{height}mm</option>
                            ))}
                        </select>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 'var(--space-4)'
                    }}>
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 'var(--font-weight-medium)',
                                marginBottom: 'var(--space-2)'
                            }}>
                                Sheet Type
                            </label>
                            <select
                                name="sheetType"
                                value={formData.sheetType}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: 'var(--space-3)',
                                    border: '1px solid var(--border-medium)',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: 'var(--font-size-base)'
                                }}
                            >
                                {sheetTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 'var(--font-weight-medium)',
                                marginBottom: 'var(--space-2)'
                            }}>
                                Post Type
                            </label>
                            <select
                                name="postType"
                                value={formData.postType}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: 'var(--space-3)',
                                    border: '1px solid var(--border-medium)',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: 'var(--font-size-base)'
                                }}
                            >
                                {postTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Column - Results */}
            <div style={{
                background: 'var(--bg-secondary)',
                padding: 'var(--space-6)',
                borderRadius: 'var(--radius-lg)'
            }}>
                <h3 style={{
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: 'var(--font-weight-bold)',
                    marginBottom: 'var(--space-4)'
                }}>
                    Calculation Results
                </h3>

                <div style={{
                    display: 'grid',
                    gap: 'var(--space-3)',
                    marginBottom: 'var(--space-6)'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span>Panels Required:</span>
                        <strong>{results.numberOfPanels}</strong>
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span>Total Coverage:</span>
                        <strong>{results.maxCover.toFixed(1)}m</strong>
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span>Posts Required:</span>
                        <strong>{results.numberOfPosts}</strong>
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: 'var(--space-3)',
                        borderTop: '1px solid var(--border-medium)'
                    }}>
                        <span style={{ fontSize: 'var(--font-size-lg)' }}>Estimated Cost:</span>
                        <strong style={{
                            fontSize: 'var(--font-size-xl)',
                            color: 'var(--primary-600)'
                        }}>
                            ${results.totalCost.toFixed(2)}
                        </strong>
                    </div>
                </div>

                <div style={{
                    display: 'flex',
                    gap: 'var(--space-3)',
                    flexDirection: 'column'
                }}>
                    <Link
                        to="/purchasing/quote"
                        className="btn btn-primary"
                        style={{ 
                            textDecoration: 'none',
                            width: '100%',
                            textAlign: 'center'
                        }}
                        onClick={onClose}
                    >
                        Get Detailed Quote
                    </Link>
                </div>
            </div>
        </div>
    );
} 